/**
 * TanStack Query hooks for the notification center.
 *
 * Query keys:
 *   ['notifications', 'list', { unread, type }]   filtered list
 *   ['notifications', 'unread-count']             cheap badge query
 *
 * Mutations invalidate both keys on success so the bell badge stays in sync.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api'

export type NotificationType =
  | 'pickup_request'
  | 'take_approved'
  | 'take_submitted'
  | 'comment_mention'
  | 'milestone_paid'
  | 'milestone_due'
  | 'batch_ready_for_review'
  | 'system'

export interface Notification {
  id: string
  organization_id: string
  type: NotificationType | string
  title: string
  body: string | null
  link: string | null
  read_at: string | null
  created_at: string
}

export interface NotificationListResponse {
  items: readonly Notification[]
  unread_count: number
}

export interface NotificationListFilters {
  unread?: boolean
  type?: NotificationType
  limit?: number
}

export const notificationsKeys = {
  root: ['notifications'] as const,
  list: (filters: NotificationListFilters) => ['notifications', 'list', filters] as const,
  unreadCount: ['notifications', 'unread-count'] as const,
}

function buildQuery(filters: NotificationListFilters): string {
  const sp = new URLSearchParams()
  if (filters.unread) sp.set('unread', 'true')
  if (filters.type) sp.set('type', filters.type)
  if (filters.limit) sp.set('limit', String(filters.limit))
  const qs = sp.toString()
  return qs ? `?${qs}` : ''
}

/**
 * List the current user's notifications. The response also carries the unread
 * count so a single fetch backs both the dropdown and the badge.
 */
export function useNotifications(filters: NotificationListFilters = {}) {
  return useQuery({
    queryKey: notificationsKeys.list(filters),
    queryFn: () => api<NotificationListResponse>(`/api/v1/notifications${buildQuery(filters)}`),
    // Refetch when the tab regains focus — cheaper than long-polling.
    refetchOnWindowFocus: true,
  })
}

/**
 * Cheap unread-count poller for the bell badge. Polls every 60s in the
 * background; if you've already loaded a list, prefer reading `unread_count`
 * off that response.
 */
export function useUnreadNotificationCount(options: { pollMs?: number } = {}) {
  const pollMs = options.pollMs ?? 60_000
  return useQuery({
    queryKey: notificationsKeys.unreadCount,
    queryFn: () => api<{ unread_count: number }>('/api/v1/notifications/unread-count'),
    refetchInterval: pollMs,
    refetchOnWindowFocus: true,
  })
}

/** Mark one notification as read. Optimistically removes it from unread lists. */
export function useMarkNotificationRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: true }>(`/api/v1/notifications/${id}/read`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notificationsKeys.root })
    },
  })
}

/** Mark every unread notification as read. */
export function useMarkAllNotificationsRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      api<{ ok: true; marked: number }>('/api/v1/notifications/read-all', {
        method: 'POST',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notificationsKeys.root })
    },
  })
}

export interface NotificationPreferences {
  email_digest_enabled: boolean
}

/** Update the caller's notification preferences. */
export function useUpdateNotificationPreferences() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (patch: Partial<NotificationPreferences>) =>
      api<NotificationPreferences>('/api/v1/me/preferences', {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['account', 'profile'] })
    },
  })
}
